// ============================================================================
// HTTP Client Interface & Types
// ============================================================================

export interface ApiResponse<T> {
  data: T;
  status: number;
}

export interface RequestConfig {
  headers?: Record<string, string>;
  timeout?: number;
  params?: Record<string, string | number | boolean | undefined>;
}

export interface IHttpClient {
  get<T>(url: string, config?: RequestConfig): Promise<ApiResponse<T>>;
  post<T>(url: string, data?: unknown, config?: RequestConfig): Promise<ApiResponse<T>>;
  put<T>(url: string, data?: unknown, config?: RequestConfig): Promise<ApiResponse<T>>;
  patch<T>(url: string, data?: unknown, config?: RequestConfig): Promise<ApiResponse<T>>;
  delete<T>(url: string, config?: RequestConfig): Promise<ApiResponse<T>>;
  getBaseUrl(): string;
  isCancelError(error: unknown): boolean;
}

// ============================================================================
// API Client Configuration
// ============================================================================

export interface RetryConfig {
  enabled: boolean;
  maxRetries: number;
  baseDelayMs: number;
  maxDelayMs: number;
  retryableStatuses: number[];
  retryableCodes: string[];
}

export const DEFAULT_RETRY_CONFIG: RetryConfig = {
  enabled: false,
  maxRetries: 3,
  baseDelayMs: 1000,
  maxDelayMs: 10000,
  retryableStatuses: [500, 502, 503, 504],
  retryableCodes: ['ERR_NETWORK', 'ECONNABORTED', 'ETIMEDOUT'],
};

export interface ApiClientConfiguration {
  baseUrl: string;
  timeout: number;
  retry: RetryConfig;
}

// ============================================================================
// API Error
// ============================================================================

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status?: number,
    public readonly code?: string,
    public readonly details?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
    Object.setPrototypeOf(this, ApiError.prototype);
  }

  isStatus(status: number): boolean {
    return this.status === status;
  }

  isServerError(): boolean {
    return this.status !== undefined && this.status >= 500;
  }

  isClientError(): boolean {
    return this.status !== undefined && this.status >= 400 && this.status < 500;
  }

  isUnauthorized(): boolean {
    return this.status === 401;
  }

  isForbidden(): boolean {
    return this.status === 403;
  }

  isNotFound(): boolean {
    return this.status === 404;
  }
}

// ============================================================================
// API Error Events
// ============================================================================

export type ApiErrorType = 'network' | 'server' | 'timeout' | 'unauthorized' | 'unknown';

export interface ApiErrorEventDetail {
  type: ApiErrorType;
  message: string;
  status?: number;
  code?: string;
  url?: string;
}

export function categorizeApiError(status?: number, code?: string): ApiErrorType {
  if (!status && code === 'ERR_NETWORK') {
    return 'network';
  }
  if (code === 'ECONNABORTED' || code === 'ETIMEDOUT') {
    return 'timeout';
  }
  if (status === 401) {
    return 'unauthorized';
  }
  if (status !== undefined && status >= 500) {
    return 'server';
  }
  return 'unknown';
}
